/**
 * 마이페이지 "Country & Language" 드로어 배선 — 초기값 결정 + 변경 저장.
 *
 * 값이 사는 곳은 둘이고(기기 쿠키 · 서버 프로필), 여기가 그 둘을 잇는 유일한 자리다.
 * 드로어(snippets/piyonna-country-language-drawer)는 화면만 알고 저장소를 모른다.
 *
 * 규약 전문: docs/APP-PROXY-API.md (서버) · docs/APP-BRIDGE.md §3.3 (앱)
 *
 * ── 초기값 우선순위 ──
 * 뒤 단계가 앞 단계를 덮는다. 왕복을 기다리지 않으므로 필드가 비어 보이는 순간이 없다.
 *
 * | # | 출처 | 국가 | 언어 |
 * | --- | --- | --- | --- |
 * | 0 | Liquid (즉시) | 미선택 placeholder | 현재 지면 로케일 |
 * | 1 | 기기 쿠키 (동기) | `piyonna_country` | `piyonna_locale` |
 * | 2 | 서버 `customer_profile` (성공 시 **쿠키까지 갱신**) | `country_code` | `language` |
 * | 3 | 접속 IP (국가가 1·2 어디에도 없을 때만) | `getClientCountryCode()` | — |
 *
 * 3단계까지 가도 국가를 못 정하면 **비워 둔다.** IP 국가가 시트 목록(24개국)에 없는 경우가 그렇다.
 *
 * ── 변경 시 처리 순서 ──
 * 사용자가 시트에서 값을 고르면 세 가지를 차례로 한다.
 *   1. 서버 저장 `saveCustomerCountryLanguage` — 전체 교체라 국가·언어를 **항상 함께** 보낸다
 *   2. 기기 쿠키 갱신 — 서버 저장이 실패해도(미로그인 등) 한다. 기기 저장은 로그인과 무관하다.
 *      국가는 `piyonna_country` 와 Shopify `localization`(마켓) 을 함께 쓴다 — piyonna-locale-store 참고
 *   3. 앱 통지 `PiyonnaApp.setLanguage` — **언어가 바뀐 경우에만.** 국가용 명령은 규약에 없다
 * 3번이 앱을 재실행시키므로(규약 §3.3) 1·2 가 끝난 뒤에 부른다. 순서가 뒤집히면 웹뷰가 먼저
 * 사라져 저장이 날아간다.
 *
 * **3번은 1번 저장의 응답을 받은 뒤 부르고, 저장 성공 · 실패와 무관하게 항상 부른다.** 성공하면
 * 응답의 언어(서버가 정규화한 값)를, 실패하면 고른 언어를 보낸다. 사용자가 고른 언어는 저장 결과와
 * 상관없이 앱에 바로 반영한다는 결정이다. **언어 변경의 저장은 3초만 기다린다**
 * (`LANGUAGE_SAVE_TIMEOUT_MS`) — 응답이 늦어도 사용자가 앱 전환을 오래 기다리지 않게 하려는 것이고,
 * 3초가 지나면 TIMEOUT 실패로 보고 고른 언어로 부른다. 국가만 바꿀 때는 기본 타임아웃(10초)이다.
 * 비로그인은 1번이 PERMISSION_DENIED 로 즉시 실패하므로
 * 기기 언어만 바뀐다.
 * ⚠️ 로그인 상태에서 저장이 실패하면 계정 언어는 이전 값으로 남는다 — 다음 로그인(재설치 포함)에서
 *    app-language.js 가 계정 언어로 되돌린다(FP-1468 — 언어의 기준은 계정 프로필).
 *
 * 저장이 끝나면 **국가를 바꾼 경우에만** `Changes saved.` 토스트를 띄운다(FP-1362).
 * 언어는 확인 팝업으로 바꾸고 곧바로 앱이 재실행되므로(3번) 토스트가 남을 화면이 없다.
 *
 * ── 알아둘 것 ──
 * · **아직 붙지 않은 것: 웹 지면의 로케일 전환(Shopify localization form POST)과 저장 실패 알림.**
 *   언어를 바꿔도 보고 있는 지면의 언어는 그대로다.
 * · 조회·저장 실패는 조용히 넘긴다. 오류 적재는 piyonna-api.js 가 이미 한다.
 * · `shopify theme dev`(9292) 에서는 서버(앱 미설치 404)와 IP 조회(엣지 미경유 404) 둘 다 실패한다.
 *   그 환경의 정상 상태이고, 쿠키 경로만 동작한다. 실제 확인은 운영·베타 프리뷰에서 한다.
 * · 시트 목록에 없는 코드를 서버·쿠키가 갖고 있으면 화면에는 못 그린다(이름·국기가 없다).
 *   그때도 그 값을 **저장 페이로드에는 그대로 실어** 사용자가 국가를 직접 고르기 전까지 보존한다.
 *   그래서 3단계 IP 추정은 "저장된 값이 아무것도 없을 때"만 돌린다 — 돌리면 남의 값을 덮는다.
 */

import { fetchCustomerProfile, saveCustomerCountryLanguage } from '@theme/piyonna-api-mypage';
import {
  readCountryCode,
  readLanguageCode,
  writeCountryCode,
  writeLanguageCode,
} from '@theme/piyonna-locale-store';
// client-country.js 는 importmap 에 없다(docs/COMPONENTS.md › client-country). 상대 경로로 부른다.
import { getClientCountryCode } from './client-country.js';

/**
 * 저장된 국가 코드 원본. 시트 목록에 없어 화면에 못 그린 값도 여기 남는다.
 * 전체 교체 저장에서 사용자가 건드리지 않은 국가를 지우지 않기 위한 값이다.
 * @type {string|null}
 */
let storedCountryCode = null;

/**
 * 언어 변경 시 서버 저장을 기다리는 상한. 넘기면 실패로 보고 setLanguage 를 부른다 — 파일 머리 주석
 * 「변경 시 처리 순서」 참고. 앱이 곧 재실행되므로 늦게 도착한 응답을 받을 화면은 남지 않는다.
 */
const LANGUAGE_SAVE_TIMEOUT_MS = 3000;

/** 변경 저장을 한 줄로 세운다. 국가·언어를 잇달아 바꿔도 마지막 상태가 서버에 남는다. */
let saveQueue = Promise.resolve();

/** 드로어가 없는 지면(다른 섹션에 잘못 실림)에서는 아무것도 하지 않는다. */
function drawerIsReady() {
  return (
    typeof window.applyPiyonnaCountryLanguageProfile === 'function' &&
    typeof window.readPiyonnaCountryLanguageFields === 'function'
  );
}

/** 드로어에 값을 세운다. 시트 목록에 없는 코드는 드로어가 알아서 무시한다. */
function apply(countryCode, languageCode) {
  window.applyPiyonnaCountryLanguageProfile({
    country_code: countryCode,
    language: languageCode,
  });
}

/** 1단계 — 기기 쿠키. 동기라 첫 페인트 직후 바로 선다. */
function applyDeviceValues() {
  storedCountryCode = readCountryCode();
  apply(storedCountryCode, readLanguageCode());
}

/**
 * 2단계 — 서버 프로필. 성공하면 그 값이 이긴다(로그인 계정이 기준이므로) 그리고 쿠키를 맞춘다.
 * @returns {Promise<void>}
 */
async function applyServerValues() {
  let profile;
  try {
    profile = await fetchCustomerProfile();
  } catch {
    // 미로그인·404·타임아웃. 쿠키 값이 그대로 남는다.
    return;
  }

  if (!profile) return; // 저장한 적 없음. 오류가 아니다

  if (profile.country_code) {
    storedCountryCode = profile.country_code;
    writeCountryCode(profile.country_code);
  }
  if (profile.language) {
    writeLanguageCode(profile.language);
  }

  apply(profile.country_code, profile.language);
}

/**
 * 3단계 — 접속 IP 국가. 어디에도 저장된 국가가 없을 때만 부른다.
 *
 * 추정값이므로 **쿠키·서버에 저장하지 않는다.** 사용자가 고른 값이 아니기 때문이다.
 * @returns {Promise<void>}
 */
async function applyClientCountry() {
  if (storedCountryCode) return;

  const countryCode = await getClientCountryCode();
  if (!countryCode) return; // 조회 실패 — 비워 둔다

  apply(countryCode, null);
}

/**
 * 변경을 세 저장소에 흘린다. 필드에 서 있는 값을 그때그때 읽으므로,
 * 큐에 밀려 늦게 실행돼도 최신 상태가 저장된다.
 * @param {boolean} languageChanged - 앱 통지(setLanguage) 여부를 가른다
 */
async function persist(languageChanged) {
  const fields = window.readPiyonnaCountryLanguageFields();
  // 미선택은 빈 문자열로 온다. 저장 계약은 "지움"을 null 로 표현한다.
  // 화면에 못 그린 저장값(storedCountryCode)이 있으면 그것을 보존한다.
  const countryCode = fields.country_code || storedCountryCode || null;
  const languageCode = fields.language || null;

  // 1. 서버. 실패해도 2번은 그대로 진행한다 — 기기 저장은 로그인과 무관하다.
  /** @type {import('@theme/piyonna-api-mypage').CustomerProfile|null} */
  let saved = null;
  try {
    saved = await saveCustomerCountryLanguage(
      { country_code: countryCode, language: languageCode },
      // 언어 변경은 3초만 기다린다. undefined 면 piyonna-api 기본값(10초)
      { timeoutMs: languageChanged ? LANGUAGE_SAVE_TIMEOUT_MS : undefined }
    );
  } catch {
    // 오류 적재는 piyonna-api.js 가 한다. 사용자 알림은 아직 붙지 않았다.
  }

  // 2. 기기.
  storedCountryCode = countryCode;
  writeCountryCode(countryCode);
  writeLanguageCode(languageCode);

  // 3. 앱. 언어가 실제로 바뀐 경우에만 — 앱이 초기 설정부터 다시 실행된다(규약 §3.3).
  if (!languageChanged || !languageCode || !window.PiyonnaApp) return;

  // 저장 응답을 받은 뒤 항상 부른다. 성공이면 응답의 언어, 실패면 고른 언어 — 위 「변경 시 처리 순서」 참고
  window.PiyonnaApp.setLanguage(saved?.language || languageCode);
}

/**
 * 저장 완료 토스트. 문구는 Liquid 가 드로어의 data 속성으로 넘긴다 —
 * 에셋에서는 `t` 필터를 쓸 수 없다(delete-account 배선과 같은 방식).
 *
 * 토스트가 드로어 뒤에 깔리지 않게 옮기는 일은 드로어 스니펫이 한다.
 */
function toastSaved() {
  const drawer = document.getElementById('piyonna-country-language-drawer');
  const message = drawer?.dataset.savedMessage;
  if (!message || typeof window.showToast !== 'function') return;
  window.showToast(message, 3000);
}

/** 시트 선택을 저장 큐에 올린다. 큐가 비어 있어도 순서를 보장하기 위해 항상 체인에 잇는다. */
function enqueue(languageChanged) {
  saveQueue = saveQueue
    .then(() => persist(languageChanged))
    // 언어는 제외한다 — persist 3번이 앱을 재실행시켜 토스트를 볼 화면이 남지 않는다.
    .then(() => {
      if (!languageChanged) toastSaved();
    })
    .catch(() => {});
}

if (drawerIsReady()) {
  // 리스너를 먼저 건다. 초기값 왕복이 끝나기 전에 사용자가 고를 수 있다.
  document.addEventListener('piyonna:country-selected', () => enqueue(false));
  document.addEventListener('piyonna:language-selected', () => enqueue(true));

  applyDeviceValues();
  applyServerValues().then(applyClientCountry);
}
